import type { EngineLoader, WlEngine } from './types'

/**
 * Registry of every WL engine the app knows about. Each entry is a lazy
 * loader — the actual engine module is only fetched when a shop needs it.
 *
 * Adding a new engine:
 *   1. Create `frontend/src/wl-engines/<id>/index.ts` exporting a default
 *      `WlEngine` object
 *   2. Add one line here: `'my-engine': () => import('./my-engine').then(m => m.default),`
 *   3. Done. No consumer edits required (OCP).
 */
const REGISTRY: Record<string, EngineLoader> = {
  default:      () => import('./default').then((m) => m.default),
  'board-engine': () => import('./board-engine').then((m) => m.default),
}

/**
 * Public list of engine IDs — used by the Provider pack picker to show
 * available engines without loading their code.
 */
export function listEngineIds(): string[] {
  return Object.keys(REGISTRY)
}

/**
 * Resolve an engine by id. Falls back to the `default` engine when the id
 * isn't registered (e.g. a pack references an engine we haven't shipped yet,
 * or a shop has an ActiveWlPackId pointing at a removed engine).
 *
 * Returns the engine module, ready to render. Called ONCE per SPA lifetime
 * per engine — subsequent calls hit the module cache.
 */
export async function loadEngine(id: string | null | undefined): Promise<WlEngine> {
  const loader = REGISTRY[id ?? 'default'] ?? REGISTRY.default
  return await loader()
}
